import { Action } from 'redux';
import {
  call,
  take,
  fork,
  put,
} from 'redux-saga/effects';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import {
  getBackendUrl,
  post,
  get,
  AdditionalOptions,
} from '../apiService';

import { ReduxAction } from '../types/redux';
import { isNullOrUndefined } from './dataUtils';
import HttpError from '../types/errors';
import { notifyHttpUnauthorized } from '../actions';
import config from '../../config';

export type PerformApiArgs<T> = {
  params?: Record<string, string | string[] | undefined>;
  payload?: Record<string, unknown>;
  onResult?: (args: { result?: T, error?: Error }) => void;
};

function createUrlSearchParams(
  givenParams: Record<string, string | string[] | undefined> | undefined,
) {
  const params = givenParams;

  if (params) {
    Object.keys(params).forEach((key) => (
      isNullOrUndefined(params[key]) && delete params[key]
    ));
  }

  const urlSearchParams = new URLSearchParams(params as unknown as string);

  return urlSearchParams;
}

export function* performApiGet<T>(
  path: string,
  args?: PerformApiArgs<T>,
  options?: RequestInit,
) {
  try {
    const backendUrl = getBackendUrl();
    const params = args && args.params;

    const urlSearchParams = createUrlSearchParams(params);

    const url = `${backendUrl}${path}?${decodeURIComponent(urlSearchParams.toString())}`;
    const data = (yield call(get, url, options)) as Record<string, unknown>;

    const result = typeof data === 'object'
      ? camelcaseKeys(data, { deep: true }) as unknown as T
      : data;

    if (args && args.onResult) {
      yield args.onResult({ result });
    }
  } catch (error: any) {
    if (error instanceof HttpError
      && (error.statusCode === 401 || error.statusCode === 400)) {
      localStorage.removeItem(`site-${config.siteId}-api_token`);
      const lastSegment = window.location.pathname.split('/').pop();
      if (lastSegment !== 'login') {
        yield put(notifyHttpUnauthorized());
      }
    }

    if (args && args.onResult) {
      yield args.onResult({ error });
    }
  }
}

function parsePostBody<T>(args?: PerformApiArgs<T>, options?: RequestInit & AdditionalOptions) {
  const isMultipart = options?.useMultiPartsFormData || false;

  if (!isMultipart) {
    return args && args.payload
      ? JSON.stringify(snakecaseKeys(args.payload, { deep: true }))
      : undefined;
  }

  const data = new FormData();

  return args && args.payload
    ? Object.keys(args.payload).reduce((formData, key) => {
      formData.append(key, args.payload![key] as Blob);
      return formData;
    }, data)
    : undefined;
}

export function* performApiPost<T>(
  path: string,
  args?: PerformApiArgs<T>,
  options?: RequestInit & AdditionalOptions,
) {
  try {
    const backendUrl = getBackendUrl();
    const params = args && args.params;

    const urlSearchParams = createUrlSearchParams(params);

    const url = `${backendUrl}${path}?${decodeURIComponent(urlSearchParams.toString())}`;
    const body = parsePostBody(args, options);

    const data = (
      yield call(
        post,
        url,
        body,
        options,
      )
    ) as Record<string, unknown>;

    const result = typeof data === 'object'
      ? camelcaseKeys(data, { deep: true }) as unknown as T
      : data;

    if (args && args.onResult) {
      yield args.onResult({ result });
    }
  } catch (error: any) {
    if (error instanceof HttpError
      && (error.statusCode === 401 || error.statusCode === 400)) {
      localStorage.removeItem(`site-${config.siteId}-api_token`);
      const lastSegment = window.location.pathname.split('/').pop();
      if (lastSegment !== 'login') {
        yield put(notifyHttpUnauthorized());
      }
    }

    if (args && args.onResult) {
      yield args.onResult({ error });
    }
  }
}

export function* takeAndGetFlow<TApiReturn, TTakeActionArgs, TConstant>(
  action: TConstant,
  url: string,
  paramsCallback?: (args: TTakeActionArgs) => Record<string, string>,
  onResult?: ({ result, error }: { result?: TApiReturn, error?: Error }) => Action,
) {
  while (true) {
    const act = (yield take(String(action))) as ReduxAction<TTakeActionArgs, TConstant>;
    yield fork((args: TTakeActionArgs) => performApiGet<TApiReturn>(
      url, {
        params: paramsCallback && paramsCallback(args),
        onResult,
      },
    ), act);
  }
}

export function* takeAndPostFlow<TApiReturn, TTakeActionArgs, TConstant>(
  action: TConstant,
  url: string,
  paramsCallback?: (args: TTakeActionArgs) => Record<string, string>,
  payloadCallback?: (args: TTakeActionArgs) => Record<string, unknown>,
  onResult?: (result?: TApiReturn, error?: Error) => Action,
) {
  while (true) {
    const act = (yield take(String(action))) as ReduxAction<TTakeActionArgs, TConstant>;
    yield fork((args: TTakeActionArgs) => performApiPost<TApiReturn>(
      url, {
        params: paramsCallback && paramsCallback(args),
        payload: payloadCallback && payloadCallback(args),
        onResult: ({ result, error }) => onResult && onResult(result, error),
      },
    ), act);
  }
}
